/**
 * Claude Code usage ranking cards.
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-usage dashboard のランキングカード群を描画する presentation component
 * @graph-connects none
 */

import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import { formatCompact, formatNumber } from './formatters.js';

/** 順位カードの 1 行 */
export interface CcUsageRankingRow {
  key: string;
  primary: string;
  secondary?: string;
  tokens: number;
  /** 補助メトリクス (例: メッセージ数 / ユーザー数 など) */
  secondaryMetric?: number;
  secondaryMetricLabel?: string;
}

/** 1 つの順位カード分の指定 */
export interface CcUsageRanking {
  title: string;
  emptyLabel: string;
  rows: readonly CcUsageRankingRow[];
}

/**
 * 順位カードの集合を 1〜2 カラムで並べる panel。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-usage ランキングタブ用の presentation
 * @graph-connects none
 */
export function CcUsageRankingsPanel({ rankings }: { rankings: readonly CcUsageRanking[] }) {
  if (rankings.length === 0) return null;
  const gridClass =
    rankings.length >= 3
      ? 'grid gap-4 lg:grid-cols-2 xl:grid-cols-3'
      : rankings.length === 2
        ? 'grid gap-4 lg:grid-cols-2'
        : 'grid gap-4';
  return (
    <div className={gridClass}>
      {rankings.map((r) => (
        <RankingCard key={r.title} ranking={r} />
      ))}
    </div>
  );
}

/**
 * 順位付きランキングカード。各行に rank badge + 主従ラベル + bar + 数値。
 *
 * @graph-connects none
 */
function RankingCard({ ranking }: { ranking: CcUsageRanking }) {
  const { title, emptyLabel } = ranking;
  const rows = [...ranking.rows].sort((a, b) => b.tokens - a.tokens);
  const maxTokens = rows.reduce((acc, row) => Math.max(acc, row.tokens), 0);
  return (
    <Card padding="none">
      <CardHeader className="!mt-0 px-4 pt-4">
        <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-text-muted)]">
          {title}
        </h2>
      </CardHeader>
      <CardContent className="!mt-2 px-2 pb-2">
        {rows.length === 0 ? (
          <p className="px-2 py-6 text-center text-sm text-[var(--color-text-muted)]">
            {emptyLabel}
          </p>
        ) : (
          <ol className="flex flex-col">
            {rows.map((row, idx) => {
              const rank = idx + 1;
              const ratio = maxTokens === 0 ? 0 : (row.tokens / maxTokens) * 100;
              return (
                <li
                  key={row.key}
                  className="group flex items-center gap-3 rounded-md px-2 py-1.5 transition-colors hover:bg-[var(--color-bg-hover)]"
                >
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums ${rankBadgeClass(rank)}`}
                  >
                    {rank}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="truncate text-sm font-medium text-[var(--color-text-primary)]">
                        {row.primary}
                      </span>
                      {row.secondary ? (
                        <span className="truncate text-[11px] text-[var(--color-text-muted)]">
                          {row.secondary}
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-[var(--color-bg-base)]">
                      <div
                        className="h-full rounded-full bg-[var(--color-accent-primary)]"
                        style={{ width: `${ratio.toString()}%` }}
                      />
                    </div>
                  </div>
                  <div className="flex w-28 shrink-0 flex-col items-end">
                    <span className="text-sm font-semibold tabular-nums text-[var(--color-text-primary)]">
                      {formatCompact(row.tokens)}
                    </span>
                    {row.secondaryMetric !== undefined && row.secondaryMetricLabel ? (
                      <span className="text-[10px] tabular-nums text-[var(--color-text-muted)]">
                        {formatNumber(row.secondaryMetric)} {row.secondaryMetricLabel}
                      </span>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * 順位バッジの配色 (1, 2, 3 だけ特別扱い)。
 * @graph-connects none
 */
function rankBadgeClass(rank: number): string {
  if (rank === 1) return 'bg-amber-500/20 text-amber-300 ring-1 ring-inset ring-amber-500/40';
  if (rank === 2) return 'bg-slate-400/20 text-slate-200 ring-1 ring-inset ring-slate-400/40';
  if (rank === 3) return 'bg-orange-600/20 text-orange-300 ring-1 ring-inset ring-orange-600/40';
  return 'bg-[var(--color-bg-base)] text-[var(--color-text-muted)]';
}
